import React, { useEffect, useState } from 'react';
import { MenuItem, Page } from '@tassomutuo-backoffice/types';
import {
  CtaButton,
  ModalContainer,
  TextInput,
  AutocompleteSelectInput,
} from '@tassomutuo-backoffice/ui';
import { Box, IconButton, Typography } from '@mui/material';
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import { alpha } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AddIcon from '@mui/icons-material/Add';

interface IFormInputs {
  name: string;
  page?: string;
  subItems: { name: string; page: string }[];
}

interface IProps {
  open: boolean;
  handleClose: () => void;
  pages: Page[];
  createMenuItem: (data: IFormInputs) => Promise<void>;
  updateMenuItem: ({
    menuItemId,
    data,
  }: {
    menuItemId: string;
    data: IFormInputs;
  }) => Promise<void>;
  menuItemToUpdate: MenuItem | null;
}

const formDefaultValues = {
  name: '',
  page: '',
  subItems: [],
};

const MenuVoiceFormModal = ({
  open,
  handleClose,
  pages,
  createMenuItem,
  updateMenuItem,
  menuItemToUpdate,
}: IProps) => {
  const {
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm<IFormInputs>();

  const {
    fields: subItemsFields,
    append: appendSubItem,
    remove: removeSubItem,
    swap: swapSubItem,
  } = useFieldArray({
    control,
    name: 'subItems',
  });

  useEffect(() => {
    if (menuItemToUpdate) {
      reset({
        name: menuItemToUpdate.name,
        page: menuItemToUpdate?.page?.id || '',
        subItems: menuItemToUpdate.subItems.map((i) => ({
          name: i.name,
          page: i?.page?.id || '',
        })),
      });
    }
  }, [menuItemToUpdate]);

  const [isLoading, setIsLoading] = useState(false);

  const onAddSubItemPress = () => appendSubItem({ name: '', page: '' });

  const onCloseHandler = () => {
    handleClose();
    reset(formDefaultValues);
  };

  const onSubmit: SubmitHandler<IFormInputs> = async (data) => {
    try {
      setIsLoading(true);
      if (isEditMode) {
        await updateMenuItem({ menuItemId: menuItemToUpdate.id, data });
      } else {
        await createMenuItem(data);
      }
      onCloseHandler();
    } finally {
      setIsLoading(false);
    }
  };

  const isEditMode = !!menuItemToUpdate;

  const pagesSelectOptions = [
    //{ label: 'Seleziona pagina', value: '' },
    ...pages.map((p) => ({
      label: p.path,
      value: p.id,
    })),
  ];

  return (
    <ModalContainer
      open={open}
      handleClose={onCloseHandler}
      title={isEditMode ? 'Modifica voce menu' : 'Aggiungi voce menu'}
      containerSx={{ maxWidth: '800px' }}
      mtHeader={0}
    >
      <Box
        width="100%"
        component="form"
        maxHeight="60vh"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Box display="flex" gap={2}>
          <Controller
            control={control}
            name="name"
            rules={{
              required: 'Campo obbligatorio',
            }}
            defaultValue=""
            render={({ field }) => (
              <TextInput
                type="text"
                label="Nome voce menu"
                helperText={errors?.name?.message}
                error={!!errors?.name}
                {...field}
              />
            )}
          />

          <Controller
            control={control}
            name="page"
            defaultValue=""
            render={({ field }) => (
              <AutocompleteSelectInput
                label="Pagina"
                helperText={errors?.page?.message}
                error={!!errors?.page}
                selectOptions={pagesSelectOptions}
                {...field}
                value={pagesSelectOptions.find((p) => p.value === field.value)}
                /* eslint-disable-next-line */
                // @ts-expect-error
                onChange={(
                  event: React.SyntheticEvent,
                  /* eslint-disable-next-line */
                  value: any,
                  reason: string,
                  details?: string
                ) => {
                  field.onChange(value?.value || '');
                }}
              />
            )}
          />
        </Box>

        <Box
          sx={{
            bgcolor: (th) => alpha(th.palette.primary.dark, 0.2),
            p: 2,
            mt: 2,
          }}
        >
          <Typography mb={2} fontWeight="bold">
            Voci di secondo livello
          </Typography>
          {subItemsFields.map((item, index) => (
            <Box key={item.id} display="flex" alignItems="center" gap={2}>
              <Controller
                control={control}
                name={`subItems.${index}.name`}
                rules={{
                  required: 'Campo obbligatorio',
                }}
                defaultValue=""
                render={({ field }) => (
                  <TextInput
                    type="text"
                    label="Nome voce menu"
                    helperText={
                      errors?.subItems &&
                      errors?.subItems[index] &&
                      errors?.subItems[index]?.name?.message
                    }
                    error={
                      !!(
                        errors?.subItems &&
                        errors?.subItems[index] &&
                        errors?.subItems[index]?.name?.message
                      )
                    }
                    {...field}
                  />
                )}
              />
              <Controller
                control={control}
                name={`subItems.${index}.page`}
                rules={{
                  required: 'Campo obbligatorio',
                }}
                defaultValue=""
                render={({ field }) => (
                  <AutocompleteSelectInput
                    label="Pagina"
                    helperText={
                      errors?.subItems &&
                      errors?.subItems[index] &&
                      errors?.subItems[index]?.page?.message
                    }
                    error={
                      !!(
                        errors?.subItems &&
                        errors?.subItems[index] &&
                        errors?.subItems[index]?.page?.message
                      )
                    }
                    selectOptions={pagesSelectOptions}
                    {...field}
                    value={pagesSelectOptions.find(
                      (p) => p.value === field.value
                    )}
                    /* eslint-disable-next-line */
                    // @ts-expect-error
                    onChange={(
                      event: React.SyntheticEvent,
                      /* eslint-disable-next-line */
                      value: any,
                      reason: string,
                      details?: string
                    ) => {
                      field.onChange(value?.value || '');
                    }}
                  />
                )}
              />

              <Box display="flex">
                {subItemsFields.length > 1 && (
                  <>
                    <IconButton onClick={() => swapSubItem(index, index + 1)}>
                      <ArrowDownwardIcon />
                    </IconButton>
                    <IconButton onClick={() => swapSubItem(index, index - 1)}>
                      <ArrowUpwardIcon />
                    </IconButton>
                  </>
                )}
                <IconButton onClick={() => removeSubItem(index)}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Box>
          ))}

          <CtaButton onClick={onAddSubItemPress}>
            <AddIcon />
            Aggiungi voce
          </CtaButton>
        </Box>
        <Box display="flex" justifyContent="center" my={4}>
          <CtaButton variant="contained" type="submit" loading={isLoading}>
            SALVA
          </CtaButton>
        </Box>
      </Box>
    </ModalContainer>
  );
};

export default MenuVoiceFormModal;
